// context/AccessibilityContext.tsx
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getSystem } from '@/system';

import ScreenReaderAnnouncement from './ScreenReaderAnnouncement';
import { TTS } from './speechSynthesizer';

// Stable callbacks only. `currentAnnouncement` is excluded on purpose: it changes on
// every announcement and again when the timer clears it, which re-rendered every
// consumer in the tree. Its only reader, ScreenReaderAnnouncement, takes it as a prop.
type AccessibilityContextType = {
  announceToScreenReader: (
    message: string,
    priority?: 'polite' | 'assertive',
    queue?: boolean
  ) => void;
  clearAnnouncement: () => void;
  overrideScreenReaderAnnouncement: (message: string) => void;
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(
  undefined
);

const system = getSystem();

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [currentAnnouncement, setCurrentAnnouncement] = useState<{
    message: string;
    priority: 'polite' | 'assertive';
  } | null>(null);

  const queueRef = useRef('');

  const announceToScreenReader = useCallback(
    (_message: string, priority: 'polite' | 'assertive' = 'polite', queue?: boolean) => {
      const message = queueRef.current + _message;
      if (queue) queueRef.current = _message;

      if (system.id === 'hisense') {
        setTimeout(() => TTS.speak(message), 1000);
      } else {
        system.overrideAria
          ? TTS.speak(message)
          : setCurrentAnnouncement({ message, priority });
      }

      if (!queue) queueRef.current = '';
    },
    []
  );

  const overrideScreenReaderAnnouncement = useCallback((message: string) => {
    if (system.overrideAria) TTS.speak(message);
  }, []);

  useEffect(() => {
    if (currentAnnouncement) {
      const timer = setTimeout(() => {
        setCurrentAnnouncement(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentAnnouncement]);

  const clearAnnouncement = useCallback(() => {
    setCurrentAnnouncement(null);
  }, []);

  // All three callbacks are useCallback([])-stable, so this identity never changes.
  const value = useMemo(
    () => ({
      announceToScreenReader,
      clearAnnouncement,
      overrideScreenReaderAnnouncement,
    }),
    [announceToScreenReader, clearAnnouncement, overrideScreenReaderAnnouncement]
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      <ScreenReaderAnnouncement
        message={currentAnnouncement?.message || null}
        priority={currentAnnouncement?.priority}
      />
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}
